import { createContext, useContext, useCallback, useMemo } from 'react'
import { useBooleanState } from '@retailer-platform/shared-components'
import { useLocalForageKey } from '../../local-storage/useLocalForageKey.hooks'
import { useSessionStorage } from '../../local-storage/useSessionStorage.hooks'
import { type ICPlatformVersion } from '../../ic-platform/versioning'

export type AdminControlsTemporarySettings = {
  forcedCurrentICPlatformVersion?: ICPlatformVersion
}
export type AdminControlsSettings = {
  showNavAccessControlSettings: boolean
  showContactHeader: boolean
  aiAgentEnabled: boolean
}

type MergedAdminControlSettings = AdminControlsSettings & AdminControlsTemporarySettings

export type AdminControlsContext = MergedAdminControlSettings & {
  setAdminControl: <K extends keyof AdminControlsSettings>(
    k: K,
    v: AdminControlsSettings[K]
  ) => void
  setTemporaryAdminControl: <K extends keyof AdminControlsTemporarySettings>(
    k: K,
    v: AdminControlsTemporarySettings[K]
  ) => void
  isAdminControlsOpen: boolean
  openAdminControls: () => void
  closeAdminControls: () => void
}

export const AdminControlsContext = createContext<AdminControlsContext | null>(null)

export const AdminControlsProvider = ({ children }) => {
  const { data: temporarySettings, setData: setTemporaryAdminControls } =
    useSessionStorage<AdminControlsTemporarySettings>('adminControls.temporarySettings')
  const { data: settings, setData: setAdminControls } =
    useLocalForageKey<AdminControlsSettings>('adminControls.settings')

  const mergedSettings = useMemo(() => {
    const defaults = {
      // Defaults go here
      showNavAccessControlSettings: false,
      showContactHeader: true,
      aiAgentEnabled: true,
    }

    const result: MergedAdminControlSettings = {
      ...defaults,
      ...settings,
      ...temporarySettings,
    }

    return result
  }, [settings, temporarySettings])

  const setAdminControl: AdminControlsContext['setAdminControl'] = useCallback(
    (key, value) => {
      setAdminControls({ ...settings, [key]: value })
    },
    [setAdminControls, settings]
  )

  const setTemporaryAdminControl: AdminControlsContext['setTemporaryAdminControl'] = useCallback(
    (key, value) => {
      setTemporaryAdminControls({ ...temporarySettings, [key]: value })
    },
    [setTemporaryAdminControls, temporarySettings]
  )

  const [isAdminControlsOpen, openAdminControls, closeAdminControls] = useBooleanState(false)

  return (
    <AdminControlsContext.Provider
      value={{
        ...mergedSettings,
        setAdminControl,
        setTemporaryAdminControl,
        isAdminControlsOpen,
        openAdminControls,
        closeAdminControls,
      }}
    >
      {children}
    </AdminControlsContext.Provider>
  )
}

export const useAdminControls = () => {
  const adminControlsContext = useContext(AdminControlsContext)
  if (!adminControlsContext) {
    throw new Error('useAdminControls must be used within an AdminControlsProvider')
  }
  return adminControlsContext
}
